// XSelfShareButton — Producer reverse-loop share button.
//
// Tap once → backend awards +1 credit (capped daily) → we open the
// pre-built X intent URL in a new tab so they can post immediately.
// We trust the click (X gives no callback signal anyway); the
// caps on the backend (per-video/day + per-producer/day) limit abuse.
//
// Hides itself for non-Producers (the perk is part of the Producer
// loyalty loop) and for videos already self-shared today.
//
// Props:
//   videoId           — id of the producer's own video.
//   isProducer        — boolean; component returns null when false.
//   usedToday         — Set<string> of video ids the producer already
//                       self-shared today; we render a "Shared ✓" pill
//                       instead of an actionable button when included.
//   remainingToday    — number; when 0, button shows as locked.
//   onShared          — callback(videoId, reward, remainingAfter) so
//                       the parent can update its own usedToday set,
//                       reward toast, and refresh credit balance.
import React, { useState } from "react";
import { Coins, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/apiClient";

const XLogo = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path
      fill="currentColor"
      d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
    />
  </svg>
);

const XSelfShareButton = ({ videoId, isProducer, usedToday, remainingToday, onShared }) => {
  const [busy, setBusy] = useState(false);

  if (!isProducer) return null;

  const alreadyShared = usedToday?.has?.(videoId);
  const locked = (remainingToday ?? 1) <= 0 && !alreadyShared;

  if (alreadyShared) {
    return (
      <span
        className="px-1.5 py-1 rounded bg-emerald-500/15 text-emerald-300 text-[10px] font-medium inline-flex items-center gap-1"
        title="Shared to X today — earn another +1 credit on this video tomorrow."
        data-testid={`x-self-share-done-${videoId}`}
      >
        <XLogo className="h-3 w-3" />
        Shared
      </span>
    );
  }

  const handle = async (e) => {
    e.stopPropagation();
    if (busy || locked) return;
    setBusy(true);
    try {
      const res = await api.post("/x/share/self", { video_id: videoId });
      const { intent_url, reward_credits, remaining_today } = res.data || {};
      // Open X in a new tab — popup-style window is fine, no need to
      // wait for postMessage since the credit was already granted.
      if (intent_url) window.open(intent_url, "_blank", "noopener,noreferrer");
      toast.success(`+${reward_credits} credit — share away! (${remaining_today} left today)`);
      onShared?.(videoId, reward_credits, remaining_today);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 429) {
        toast.info(typeof detail === "string" ? detail : "Daily cap reached.");
      } else if (status === 403) {
        toast.error(typeof detail === "string" ? detail : "Producer tier required.");
      } else if (status === 404) {
        toast.error("Video not found.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Couldn't start the X share.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={handle}
      disabled={busy || locked}
      data-testid={`x-self-share-btn-${videoId}`}
      title={
        locked
          ? "Daily share cap reached — resets at 00:00 UTC."
          : "Share to X and earn +1 credit (capped daily)."
      }
      className={`p-1.5 rounded transition-colors inline-flex items-center gap-1 ${
        locked
          ? "bg-gray-800/60 text-gray-600 cursor-not-allowed"
          : "bg-gray-800 text-sky-300 hover:bg-sky-500/15 hover:text-sky-200"
      }`}
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <XLogo className="h-4 w-4" />}
      <Coins className="h-3 w-3 text-amber-400" />
    </button>
  );
};

export default XSelfShareButton;
